import {
  createContext,
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { LineageState, LineageType, SqlLineage } from "./types";
import { UnknownAction } from "@reduxjs/toolkit";
import lineageSlice, { setTheme } from "./LineageSlice";
import { Column, StaticLineage, Table } from "../types";
import { ReactFlowInstance } from "reactflow";
import DynamicLineageComponent from "../components/DynamicLineage";
import StaticLineageComponent from "../components/StaticLineage";
import SqlLineageComponent from "../components/SqlLineage";

export interface LineageProviderProps {
  dynamicLineage?: { node?: Table; aiEnabled: boolean };
  sqlLineage?: SqlLineage;
  staticLineage?: StaticLineage;
  theme: "dark" | "light";
  lineageType: LineageType;
  allowSyncColumnsWithDB?: boolean;
  externalSidePanel?: boolean;
  renderTags?: (entity: { column: Column; table_rk: string }) => ReactNode;
  client?: "saas" | "extension";
}

interface ContextProps {
  state: LineageState;
  dispatch: React.Dispatch<UnknownAction>;
  rerender: () => void;
}

export const LineageContext = createContext<ContextProps>({
  state: lineageSlice.getInitialState(),
  dispatch: () => null,
  rerender: () => null,
});

export interface LineageRef {
  rerender: () => void;
}

const LineageProvider: ForwardRefRenderFunction<LineageRef, LineageProviderProps> = (
  {
    theme = "dark",
    lineageType,
    sqlLineage,
    dynamicLineage,
    staticLineage,
    allowSyncColumnsWithDB,
    externalSidePanel = false,
    renderTags,
    client = "extension",
  },
  lineageRef
) => {
  const [state, dispatch] = useReducer(lineageSlice.reducer, {
    ...lineageSlice.getInitialState(),
    theme,
    lineageType,
    sqlLineage,
    allowSyncColumnsWithDB,
    externalSidePanel,
    renderTags,
    client,
  });

  const flow = useRef<ReactFlowInstance<unknown, unknown>>();
  const [_rerenderVal, _rerender] = useState(0);
  const rerender = useCallback(() => _rerender(x => (x + 1) % 100), []);

  useEffect(() => {
    flow.current?.fitView({});
  }, [_rerenderVal]);

  useImperativeHandle(lineageRef, () => ({
    rerender,
  }));

  useEffect(() => {
    dispatch(setTheme(theme));
  }, [theme]);

  const values = useMemo(
    () => ({
      state,
      dispatch,
      rerender,
    }),
    [state, dispatch, rerender]
  );

  return (
    <LineageContext.Provider value={values}>
      <div className="lineage-component">
        {lineageType === "sql" && sqlLineage && (
          <SqlLineageComponent
            flow={flow}
            details={sqlLineage.details}
            tableEdges={sqlLineage.tableEdges}
            nodePositions={sqlLineage.nodePositions}
          />
        )}
        {lineageType === "dynamic" && dynamicLineage && (
          <DynamicLineageComponent flow={flow} theme={theme} />
        )}
        {lineageType === "static" && staticLineage && (
          <StaticLineageComponent flow={flow} {...staticLineage} />
        )}
        <div id="lineage-sidebar"></div>
      </div>
    </LineageContext.Provider>
  );
};

export default forwardRef(LineageProvider);

export const useLineageContext = () => {
  return useContext(LineageContext);
};

export const useLineageDispatch = () => {
  const { dispatch } = useContext(LineageContext);
  return dispatch;
};
